import { Funnel } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiPencil, PiTrash } from 'react-icons/pi';
import {
  useUpdateFunnel,
  useDeleteFunnel,
} from 'src/modules/shared/hooks/api/crm/funnels/funnel.mutation';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { FunnelPermission } from 'src/modules/shared/types/permissions.type';
import { useState } from 'react';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import {
  FunnelForm,
  FunnelFormValues,
} from 'src/modules/settings/components/molecules/funnels/funnel-form';
import { FunnelCard } from 'src/modules/settings/components/atoms/funnels/funnel-card';
import { useAlert } from 'src/contexts/alert-context';
import { FunnelSectionsVisibilityProvider } from 'src/modules/settings/contexts/funnels/sections-visibility.context';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';

interface FunnelEditCardProps {
  funnel: Funnel;
  children?: (isEditing: boolean) => React.ReactNode;
  onDirtyChange?: (dirty: boolean) => void;
  onEditingChange?: (isEditing: boolean) => void;
}

export const FunnelEditCard = ({
  funnel,
  children,
  onDirtyChange,
  onEditingChange,
}: FunnelEditCardProps) => {
  const { showAlert } = useAlert();

  const [isEditing, setIsEditing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { updateFunnel, loading: isUpdating } = useUpdateFunnel(
    () => {
      // TODO: check when implementing i18n
      showAlert('El embudo se actualizó correctamente', 'success');
    },
    (error) => {
      console.error(error);
      showAlert(
        // TODO: check when implementing i18n
        'Ocurrió un error al actualizar el embudo. Por favor, inténtalo de nuevo.',
        'error'
      );
    }
  );
  const { deleteFunnel, loading: isDeleting } = useDeleteFunnel(
    () => {
      // TODO: check when implementing i18n
      showAlert('El embudo se eliminó correctamente', 'success');
    },
    (error) => {
      console.error(error);
      showAlert(
        // TODO: check when implementing i18n
        'Ocurrió un error al eliminar el embudo. Por favor, inténtalo de nuevo.',
        'error'
      );
    }
  );

  const { hasPermission: canUpdate } = useHasPermissions(
    FunnelPermission.Update
  );
  const { hasPermission: canDelete } = useHasPermissions(
    FunnelPermission.Delete
  );

  const handleStartEditing = () => {
    setIsEditing(true);
    onEditingChange?.(true);
  };

  const handleStopEditing = () => {
    setIsEditing(false);
    // Ensure parent dirty state is cleared on cancel/stop
    onDirtyChange?.(false);
    onEditingChange?.(false);
  };

  const handleFormSubmit = async (data: FunnelFormValues) => {
    await updateFunnel({
      ...data,
      id: funnel.id,
    });

    handleStopEditing();
  };

  const handleFormCancel = () => {
    handleStopEditing();
  };

  const handleFunnelDelete = async () => {
    setShowConfirm(true);
  };

  return (
    <FunnelSectionsVisibilityProvider>
      <FunnelCard
        appearance="outline"
        variant="primary"
        className="flex flex-col gap-6"
      >
        <div className="flex justify-between items-start">
          <FunnelForm
            funnel={funnel}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            id={`funnel-form-${funnel.id}`}
            editMode={isEditing}
            isLoading={isUpdating || isDeleting}
            onDirtyChange={onDirtyChange}
          />

          {!isEditing && (
            <div className="flex items-center gap-2 shrink-0">
              {canUpdate && (
                <Button
                  variant="primary"
                  appearance="soft"
                  size="sm"
                  onClick={handleStartEditing}
                >
                  <div className="flex items-center gap-2">
                    <PiPencil className="h-4 w-4" />
                    <Text size="small">Editar</Text>
                  </div>
                </Button>
              )}

              {canDelete && (
                <Tooltip // TODO: check when implementing i18n
                  content="Eliminar embudo"
                  placement="top"
                  size="sm"
                >
                  <Button
                    variant="danger"
                    appearance="soft"
                    size="sm"
                    onClick={handleFunnelDelete}
                  >
                    <div className="flex items-center gap-2">
                      <PiTrash className="h-4 w-4" />
                      <Text size="small">Eliminar</Text>
                    </div>
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
        </div>

        {children?.(isEditing)}

        <ConfirmModal
          isOpen={showConfirm}
          onClose={() => setShowConfirm(false)}
          // TODO: check when implementing i18n
          title="Confirmar eliminación"
          variant="danger"
          size="sm"
          onConfirm={async () => {
            setShowConfirm(false);
            await deleteFunnel(funnel.id);
          }}
          // TODO: check when implementing i18n
          confirmText="Eliminar"
          // TODO: check when implementing i18n
          cancelText="Cancelar"
          loading={isDeleting}
        >
          <Text size="small">
            {/* TODO: check when implementing i18n */}
            ¿Estás seguro de querer eliminar este embudo? Esta acción es
            irreversible.
          </Text>
        </ConfirmModal>
      </FunnelCard>
    </FunnelSectionsVisibilityProvider>
  );
};
